import DateFnsUtils from '@date-io/date-fns';
import { withStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { getAnswerImages } from '../../FormDefinitionInput.utils';
import styles from './DateInput.styles';

const propTypes = {
  classes: PropTypes.object.isRequired,
  formDefinition: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  imageToEdit: PropTypes.object,
  saveAnswers: PropTypes.func.isRequired,
  updateFormHeaders: PropTypes.func.isRequired,
};

const DateInput = ({ classes, formDefinition, isReadOnly, imageToEdit, saveAnswers, updateFormHeaders }) => {
  const saveAnswersDebounceHandler = useCallback(
    debounce(newAnswers => {
      saveAnswers(newAnswers);
    }, 750),
    [],
  );
  const minDate = new Date('1700-01-01');
  const getInitialDateValue = () => {
    const value =
      formDefinition.answers.length !== 0 && formDefinition.answers[0].text !== null
        ? formDefinition.answers[0].text
        : null;
    if (!value) {
      return null;
    }

    switch (formDefinition.answerType) {
      // set to month 0 so that it takes the proper year (otherwise it will go to Dec. 31 of the previous year because of timezones
      case 'Date YY':
      case 'Date YYYY':
        return new Date(value, 0);
      case 'Date':
      case 'Date MMYY':
      default:
        return value;
    }
  };

  const handleOnChange = async (fullDate, formattedDate) => {
    let text = fullDate;

    if (['Date YY', 'Date YYYY'].includes(formDefinition.answerType)) {
      // determine if it is a valid date (and allow it to be null)
      const dateIsEmpty = !fullDate;
      const validDate = dateIsEmpty || (fullDate instanceof Date && !isNaN(fullDate));

      if (!validDate) return;

      text = dateIsEmpty ? formattedDate : parseInt(formattedDate).toString();
    }

    const answerImages = getAnswerImages(formDefinition, imageToEdit);
    const newAnswers = [
      {
        orderFormHeaderId: formDefinition.orderFormHeaderId,
        formDefinitionId: formDefinition.formDefinitionId,
        questionId: formDefinition.questionId,
        formQaAnswerId: formDefinition.answers.length !== 0 ? formDefinition.answers[0].formQaAnswerId : null,
        type: formDefinition.answerType,
        valueId: null,
        value: null,
        text,
        modifiedOn: new Date(),
        images: answerImages,
        default: false,
      },
    ];

    updateFormHeaders(newAnswers);
    saveAnswersDebounceHandler(newAnswers);
  };

  switch (formDefinition.answerType) {
    case 'Date':
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${formDefinition.orderFormHeaderId}-${formDefinition.formDefinitionId}`}
            autoOk
            variant="inline"
            format="MM/dd/yyyy"
            margin="normal"
            value={getInitialDateValue()}
            onChange={handleOnChange}
            disabled={isReadOnly}
            minDate={minDate}
          />
        </MuiPickersUtilsProvider>
      );
    case 'Date MMYY':
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${formDefinition.orderFormHeaderId}-${formDefinition.formDefinitionId}`}
            autoOk
            views={['year', 'month']}
            variant="inline"
            format="MM/yyyy"
            margin="normal"
            value={getInitialDateValue()}
            onChange={handleOnChange}
            disabled={isReadOnly}
            minDate={minDate}
          />
        </MuiPickersUtilsProvider>
      );
    case 'Date YY':
    case 'Date YYYY':
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={`fd-${formDefinition.orderFormHeaderId}-${formDefinition.formDefinitionId}`}
            autoOk
            views={['year']}
            variant="inline"
            format="yyyy"
            margin="normal"
            value={getInitialDateValue()}
            onChange={handleOnChange}
            disabled={isReadOnly}
            minDate={minDate}
          />
        </MuiPickersUtilsProvider>
      );
    default:
      console.log('This answer type is not recogized.');
      return null;
  }
};
DateInput.propTypes = propTypes;
export default withStyles(styles)(DateInput);
